<template>
  <div class="report-card-content" :aria-busy="isLoading">
    <p v-if="isLoading" role="status" class="text-muted">{{ $t('asyncFeedback.loading') }}</p>
    <div v-else-if="loadError" role="alert" class="alert alert-danger">
      {{ $t('mgmt.loadFail') }}
      <button type="button" class="btn btn-outline-primary" @click="loadTickets">{{ $t('asyncFeedback.retry') }}</button>
    </div>
    <template v-if="hasLoaded && ((!isLoading && !loadError) || ticketsData.length)">
    <div v-if="mode !== 'unfinished' && latestTicket" class="latest-ticket-card latest-ticket-featured">
      <div class="latest-card-header">
        <div>
          <div class="ticket-label">{{ $t('mainuser.detailsTitle') }}</div>
          <div class="ticket-number">{{ latestTicket.ticketNumber || '-' }}</div>
        </div>
        <span
          class="status-chip status-chip-modern"
          :style="{
            backgroundColor: getBadgeStyle(resolveItemStatus(latestTicket)).backgroundColor,
            color: getBadgeStyle(resolveItemStatus(latestTicket)).color,
            borderColor: getBadgeStyle(resolveItemStatus(latestTicket)).borderColor
          }"
        >
          <span class="status-dot" :style="{ backgroundColor: getBadgeStyle(resolveItemStatus(latestTicket)).dotColor }"></span>
          {{ getStatusText(resolveItemStatus(latestTicket)) }}
        </span>
      </div>
      <div class="ticket-meta">
        <div class="meta-row">{{ latestTicket.userPart && latestTicket.userPart.dateTimeInformed ? formatDate(latestTicket.userPart.dateTimeInformed) : '-' }}</div>
        <div class="meta-row ticket-location">
          <span class="ticket-department">{{ getCategoryName(latestTicket) }}</span>
          <span class="ticket-location-separator">|</span>
          <strong class="ticket-job-type">{{ getSubCategoryName(latestTicket) }}</strong>
        </div>
      </div>
      <div class="ticket-actions">
        <button type="button" class="card-btn track" @click="showDetail(latestTicket, latestTicket.status === 'done')">
          <CIcon name="cil-magnifying-glass" size="sm" class="mr-1" />
          {{ $t('table.user.btn.track') }}
        </button>
        <button v-if="canCancelTicket(latestTicket)" type="button" class="card-btn cancel" @click="cancelTicket(latestTicket)">
          <CIcon name="cil-trash" size="sm" class="mr-1" />
          {{ $t('table.user.btn.cancelRequest') }}
        </button>
      </div>
    </div>

    <div v-else-if="mode !== 'unfinished'" class="latest-ticket-empty latest-ticket-featured">
      <span class="empty-icon"><CIcon name="cil-inbox" size="xl" /></span>
      <div class="empty-title">{{ $t('mainuser.noLatestRequestTitle') }}</div>
      <div class="empty-text">{{ $t('mainuser.noLatestRequestText') }}</div>
    </div>

    <div v-if="mode !== 'latest' && unfinishedTickets.length" class="active-ticket-table-wrapper">
      <!-- Desktop Table View (>= 992px) -->
      <div class="d-none d-lg-block">
        <CDataTable
          ref="cDataTable"
          class="custom-pagination active-desktop-table"
          :items="unfinishedTickets"
          :fields="tableFields"
          :table-filter="{ label: $t('table.filterLabel'), placeholder: $t('table.filterPlaceholder') }"
          :items-per-page-select="itemsPerPageSelectConfig"
          :items-per-page="tableItemsPerPage"
          :active-page.sync="tablePage"
          @pagination-change="tableItemsPerPage = $event"
          hover
          pagination
          sorter
        >
          <template #ticketNumber="{ item }">
            <td :id="'ticket-row-' + (item.ticketNumber || item._id)" :data-ticket-id="item._id" :data-ticket-no="item.ticketNumber">
              <div class="table-ticket-badge">{{ item.ticketNumber || '-' }}</div>
            </td>
          </template>

          <template #reported_datetime="{ item }">
            <td>
              <div class="table-date-text">
                <CIcon name="cil-calendar" size="sm" class="mr-1" />
                {{ item.userPart && item.userPart.dateTimeInformed ? formatDate(item.userPart.dateTimeInformed) : '-' }}
              </div>
            </td>
          </template>

          <template #department_category="{ item }">
            <td>
              <div class="table-issue-info">
                <div class="table-dept-name">{{ getCategoryName(item) }}</div>
                <div class="table-subcat-name">{{ getSubCategoryName(item) }}</div>
                <div v-if="item.userPart && item.userPart.onBehalfOf" class="small text-danger mt-1">
                  ({{ $t('table.user.onBehalf') }} {{ item.userPart.onBehalfOf }})
                </div>
                <div v-if="ticketSource === 'technician'">
                  <span v-if="item._role === 'reporter'" class="role-badge badge-reporter">{{ $t('table.tech.badgeReporter') || 'งานที่แจ้งเอง' }}</span>
                  <span v-else class="role-badge badge-assignee">{{ $t('table.tech.badgeAssignee') || 'งานที่รับผิดชอบ' }}</span>
                </div>
              </div>
            </td>
          </template>

          <template #issue_user="{ item }">
            <td>
              <div class="table-problem-detail" :title="getProblemDetails(item)">
                {{ getProblemDetails(item) }}
              </div>
            </td>
          </template>

          <template #status="{ item }">
            <td class="text-center">
              <div class="table-status-cell">
                <span
                  class="status-chip status-chip-sm status-chip-modern"
                  :style="{
                    backgroundColor: getBadgeStyle(resolveItemStatus(item)).backgroundColor,
                    color: getBadgeStyle(resolveItemStatus(item)).color,
                    borderColor: getBadgeStyle(resolveItemStatus(item)).borderColor
                  }"
                >
                  <span class="status-dot" :style="{ backgroundColor: getBadgeStyle(resolveItemStatus(item)).dotColor }"></span>
                  {{ getStatusText(resolveItemStatus(item)) }}
                </span>
                <span v-if="ticketSource !== 'user' && isOverdue(item)" class="sla-overdue-pill">
                  <CIcon name="cil-clock" size="sm" class="mr-1" />
                  {{ getOverdueText(item) }}
                </span>
              </div>
            </td>
          </template>

          <template #actions="{ item }">
            <td class="text-center">
              <div class="table-action-btns">
                <button
                  type="button"
                  class="table-btn track"
                  :title="$t('table.user.btn.track')"
                  @click="showDetail(item, item.status === 'done')"
                >
                  <CIcon name="cil-magnifying-glass" size="sm" class="mr-1" />
                  {{ $t('table.user.btn.track') }}
                </button>
                <button
                  v-if="ticketSource === 'supervisor' && item.status === 'pending'"
                  type="button"
                  class="table-btn forward"
                  :title="$t('ticketDetail.supervisor.forwardOther') || 'ส่งต่อหน่วยงานอื่น'"
                  @click="$emit('forward-ticket', item)"
                >
                  <CIcon name="cil-share-alt" size="sm" class="mr-1" />
                  {{ $t('ticketDetail.supervisor.forwardOtherShort') || 'ส่งต่อ' }}
                </button>
                <button
                  v-if="canCancelTicket(item)"
                  type="button"
                  class="table-btn cancel"
                  :title="$t('table.user.btn.cancelRequest')"
                  @click="cancelTicket(item)"
                >
                  <CIcon name="cil-trash" size="sm" class="mr-1" />
                  {{ $t('table.user.btn.cancelRequest') }}
                </button>
              </div>
            </td>
          </template>

          <template #no-results-view>
            <div class="text-center my-4 text-muted">
              <div class="mb-2"><CIcon name="cil-magnifying-glass" size="xl" style="opacity: 0.4;" /></div>
              <div>{{ $t('table.noResults') || 'ไม่พบข้อมูลที่ค้นหา' }}</div>
            </div>
          </template>
        </CDataTable>
      </div>

      <!-- Mobile & iPad / Tablet Responsive Cards (< 992px) -->
      <div class="d-lg-none active-mobile-section">
        <!-- Card View Toolbar: Filter + Items per page -->
        <div class="mobile-table-toolbar">
          <div class="mobile-filter-group">
            <label class="mobile-filter-label">{{ $t('table.filterLabel') || 'ค้นหา:' }}</label>
            <div class="mobile-filter-input-wrap">
              <input
                v-model="mobileSearchQuery"
                type="text"
                class="mobile-search-input"
                :placeholder="$t('table.filterPlaceholder') || 'พิมพ์คำค้นหา...'"
              />
              <button
                v-if="mobileSearchQuery"
                type="button"
                class="mobile-search-clear"
                @click="mobileSearchQuery = ''"
              >
                ×
              </button>
            </div>
          </div>

          <div class="mobile-per-page-group">
            <label class="mobile-filter-label">{{ $t('table.itemsPerPageLabel') || 'แสดงต่อหน้า:' }}</label>
            <select v-model.number="mobileItemsPerPage" class="mobile-per-page-select">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>
        </div>

        <div v-if="mobileFilteredTickets.length" class="active-mobile-list">
          <article
            v-for="ticket in mobilePageTickets"
            :key="ticket._id"
            :id="'m-ticket-row-' + (ticket.ticketNumber || ticket._id)"
            :data-ticket-id="ticket._id"
            :data-ticket-no="ticket.ticketNumber"
            class="active-mobile-card"
            :class="'card-status--' + ticket.status"
          >
            <div class="mobile-card-head">
              <div class="mobile-ticket-badge">
                <CIcon name="cil-tag" size="sm" class="badge-icon" />
                <span>{{ ticket.ticketNumber || '-' }}</span>
              </div>
              <div class="d-flex align-items-center flex-wrap justify-content-end" style="gap: 6px;">
                <span
                  class="status-chip status-chip-modern"
                  :style="{
                    backgroundColor: getBadgeStyle(resolveItemStatus(ticket)).backgroundColor,
                    color: getBadgeStyle(resolveItemStatus(ticket)).color,
                    borderColor: getBadgeStyle(resolveItemStatus(ticket)).borderColor
                  }"
                >
                  <span class="status-dot" :style="{ backgroundColor: getBadgeStyle(resolveItemStatus(ticket)).dotColor }"></span>
                  {{ getStatusText(resolveItemStatus(ticket)) }}
                </span>
                <span v-if="ticketSource !== 'user' && isOverdue(ticket)" class="sla-overdue-pill">
                  <CIcon name="cil-clock" size="sm" class="mr-1" />
                  {{ getOverdueText(ticket) }}
                </span>
              </div>
            </div>

            <div class="mobile-card-body">
              <h5 class="mobile-subcat-title" :title="getSubCategoryName(ticket)">
                {{ getSubCategoryName(ticket) }}
              </h5>

              <div class="mobile-meta-grid">
                <div class="mobile-meta-item">
                  <CIcon name="cil-building" size="sm" class="meta-icon" />
                  <span class="meta-text dept-name">{{ getCategoryName(ticket) }}</span>
                </div>
                <div class="mobile-meta-item">
                  <CIcon name="cil-calendar" size="sm" class="meta-icon" />
                  <span class="meta-text date-text">
                    {{ ticket.userPart && ticket.userPart.dateTimeInformed ? formatDate(ticket.userPart.dateTimeInformed) : '-' }}
                  </span>
                </div>
                <div v-if="ticket.userPart && ticket.userPart.assetCode" class="mobile-meta-item asset-item">
                  <CIcon name="cil-qr-code" size="sm" class="meta-icon" />
                  <span class="meta-text asset-text">
                    {{ $t('reportDetail.assetCode') || 'รหัสครุภัณฑ์' }}: {{ ticket.userPart.assetCode }}
                  </span>
                </div>
                <div v-if="ticket.userPart && ticket.userPart.onBehalfOf" class="mobile-meta-item behalf-item">
                  <CIcon name="cil-user" size="sm" class="meta-icon" />
                  <span class="meta-text behalf-text">
                    {{ $t('table.user.onBehalf') }} {{ ticket.userPart.onBehalfOf }}
                  </span>
                </div>
                <div v-if="ticketSource === 'technician'" class="mobile-meta-item role-badge-item mt-1">
                  <span v-if="ticket._role === 'reporter'" class="role-badge badge-reporter">{{ $t('table.tech.badgeReporter') || 'งานที่แจ้งเอง' }}</span>
                  <span v-else class="role-badge badge-assignee">{{ $t('table.tech.badgeAssignee') || 'งานที่รับผิดชอบ' }}</span>
                </div>
                <div v-if="getProblemDetails(ticket) !== '-'" class="mobile-meta-item problem-item">
                  <CIcon name="cil-notes" size="sm" class="meta-icon" />
                  <span class="meta-text problem-text">{{ getProblemDetails(ticket) }}</span>
                </div>
              </div>
            </div>

            <div class="mobile-card-actions">
              <button
                type="button"
                class="card-action-btn track-btn"
                @click="showDetail(ticket, ticket.status === 'done')"
              >
                <CIcon name="cil-magnifying-glass" size="sm" class="btn-icon" />
                <span>{{ $t('table.user.btn.track') }}</span>
              </button>
              <button
                v-if="ticketSource === 'supervisor' && ticket.status === 'pending'"
                type="button"
                class="card-action-btn forward-btn"
                @click="$emit('forward-ticket', ticket)"
              >
                <CIcon name="cil-share-alt" size="sm" class="btn-icon" />
                <span>{{ $t('ticketDetail.supervisor.forwardOtherShort') || 'ส่งต่อ' }}</span>
              </button>
              <button
                v-if="canCancelTicket(ticket)"
                type="button"
                class="card-action-btn cancel-btn"
                @click="cancelTicket(ticket)"
              >
                <CIcon name="cil-trash" size="sm" class="btn-icon" />
                <span>{{ $t('table.user.btn.cancelRequest') }}</span>
              </button>
            </div>
          </article>
        </div>

        <div v-else class="text-center my-4 py-4 text-muted border rounded bg-white">
          <div class="mb-2"><CIcon name="cil-magnifying-glass" size="xl" style="opacity: 0.4;" /></div>
          <div>{{ $t('table.noResults') || 'ไม่พบข้อมูลที่ค้นหา' }}</div>
        </div>

        <div v-if="mobileTotalPages > 1" class="mobile-list-pagination">
          <button type="button" :disabled="mobilePage === 1" @click="mobilePage--">‹</button>
          <span>{{ mobilePage }} / {{ mobileTotalPages }}</span>
          <button type="button" :disabled="mobilePage === mobileTotalPages" @click="mobilePage++">›</button>
        </div>
      </div>
    </div>

    <div v-else-if="mode !== 'latest'" class="latest-ticket-empty">
      <span class="empty-icon"><CIcon name="cil-check-circle" size="xl" /></span>
      <div class="empty-title">{{ $t('mainuser.noActiveRequestTitle') }}</div>
      <div class="empty-text">{{ $t('mainuser.noActiveRequestText') }}</div>
    </div>

    <div class="status-legend mt-3">
      <div class="legend-item">
        <span class="status-circle" style="background:#e55353"></span>
        <span>{{ $t(legendKey(1)) }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#fd7e14"></span>
        <span>{{ $t(legendKey(2)) }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#ffeb3b"></span>
        <span>{{ $t(legendKey(3)) }}</span>
      </div>
      <div class="legend-item">
        <span class="status-circle" style="background:#2eb85c"></span>
        <span>{{ $t(legendKey(4)) }}</span>
      </div>
    </div>

    </template>
  </div>
</template>

<style>
.role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 4px;
}

.badge-reporter {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.badge-assignee {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

/* Active Table Styles */
.active-desktop-table.table-responsive,
.active-desktop-table .table-responsive {
  overflow-x: auto !important;
  border: 1px solid rgba(37, 99, 235, 0.09);
  border-radius: 14px;
}

.active-desktop-table table {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 820px;
  width: 100%;
}

.active-desktop-table thead th {
  padding: 14px 16px;
  border-top: 0;
  border-bottom: 1px solid rgba(37, 99, 235, 0.12);
  color: #1e40af;
  background: #f0f9ff;
  font-size: 0.88rem;
  font-weight: 800;
  vertical-align: middle;
}

.active-desktop-table tbody td {
  padding: 14px 16px;
  border-top: 0;
  border-bottom: 1px solid rgba(37, 99, 235, 0.07);
  background: #fff;
  vertical-align: middle;
  transition: background 0.18s ease;
}

.active-desktop-table tbody tr:last-child td {
  border-bottom: 0;
}

.active-desktop-table tbody tr:hover td {
  background: #f8fafc;
}

.active-desktop-table .form-control {
  min-height: 40px;
  border: 1px solid rgba(37, 99, 235, 0.17);
  border-radius: 10px;
  background: #fff;
  box-shadow: none;
}

.active-desktop-table .form-control:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.active-desktop-table .row:first-child {
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(37, 99, 235, 0.08);
  border-radius: 12px;
  background: #f8fafc;
}

.table-ticket-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 8px;
  color: #1e40af;
  background: #f0f9ff;
  font-weight: 800;
  font-size: 0.92rem;
  letter-spacing: 0.02em;
}

.table-date-text {
  display: flex;
  align-items: center;
  color: #526071;
  font-size: 0.88rem;
  white-space: nowrap;
}

.table-issue-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 220px;
}

.table-dept-name {
  color: #2f2930;
  font-weight: 800;
  font-size: 0.92rem;
  line-height: 1.4;
  word-break: normal;
  overflow-wrap: break-word;
}

.table-subcat-name {
  color: #1e40af;
  font-weight: 700;
  font-size: 0.88rem;
  line-height: 1.4;
  word-break: normal;
  overflow-wrap: break-word;
}

.table-problem-detail {
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 500;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 380px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mobile-meta-item.problem-item {
  display: flex !important;
  align-items: flex-start !important;
  text-align: left !important;
  width: 100% !important;
  gap: 8px !important;
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin-top: 2px !important;

  .meta-icon {
    flex-shrink: 0;
    margin-top: 3px;
    color: #64748b;
  }

  .problem-text {
    font-size: 0.85rem;
    color: #334155;
    font-weight: 500;
    line-height: 1.4;
    text-align: left !important;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
  }
}

.status-chip-sm {
  padding: 5px 12px !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  min-width: 90px !important;
  border-radius: 999px !important;
}

.status-chip-xs {
  padding: 4px 10px !important;
}

/* Table Action Buttons (Desktop Table View) */
.table-action-btns {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.table-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.table-btn.track {
  background: #1e40af !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.18);
}

.table-btn.track:hover {
  background: #1e3a8a !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.25);
}

.table-btn.cancel {
  background: #ffffff !important;
  color: #dc2626 !important;
  border: 1px solid rgba(220, 38, 38, 0.35) !important;
}

.table-btn.cancel:hover {
  background: #fee2e2 !important;
  border-color: #dc2626 !important;
}

.table-btn.forward {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid rgba(37, 99, 235, 0.35) !important;
}

.table-btn.forward:hover {
  background: #dbeafe !important;
  border-color: #1e40af !important;
  transform: translateY(-1px);
}

/* Featured / Latest Ticket Card Action Buttons */
.card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 10px;
  padding: 8px 18px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.card-btn.track {
  background: #1e40af !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.18);
}

.card-btn.track:hover {
  background: #1e3a8a !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.24);
}

.card-btn.cancel {
  background: #ffffff !important;
  color: #dc2626 !important;
  border: 1px solid rgba(220, 38, 38, 0.3) !important;
}

.card-btn.cancel:hover {
  background: #fee2e2 !important;
  border-color: #dc2626 !important;
}

/* Mobile & Tablet Card Layout */
.active-mobile-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 6px;
}

@media (min-width: 576px) and (max-width: 991.98px) {
  .active-mobile-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
}

.active-mobile-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.08);
  border-radius: 16px;
  padding: 16px 18px;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  overflow: hidden;
}

.active-mobile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
  border-color: rgba(37, 99, 235, 0.16);
}

/* Mobile Card Header */
.mobile-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(37, 99, 235, 0.05);
}

.mobile-ticket-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f0f9ff;
  border: 1px solid rgba(37, 99, 235, 0.12);
  border-radius: 8px;
  color: #1e40af;
  font-family: "Sarabun", sans-serif;
  font-size: 0.88rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.mobile-ticket-badge .badge-icon {
  opacity: 0.65;
  font-size: 11px;
}

/* Modern Status Chip */
.status-chip-modern {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  padding: 5px 12px !important;
  border-radius: 999px !important;
  border: 1px solid transparent !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  line-height: 1.2 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  white-space: nowrap !important;
  min-width: unset !important;
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Mobile Card Body */
.mobile-card-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.mobile-subcat-title {
  font-size: 0.98rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 10px 0;
  line-height: 1.4;
  word-break: break-word;
}

.mobile-meta-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
}

.mobile-meta-item .meta-icon {
  flex-shrink: 0;
  color: #94a3b8;
}

.mobile-meta-item .dept-name {
  color: #475569;
  font-weight: 600;
}

.mobile-meta-item .date-text {
  color: #64748b;
}

.mobile-meta-item.asset-item {
  margin-top: 2px;
  padding: 3px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  width: fit-content;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 700;
}

.mobile-meta-item.behalf-item {
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Mobile Card Actions */
.mobile-card-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed rgba(37, 99, 235, 0.08);
}

.card-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 0.86rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.card-action-btn.track-btn {
  flex: 1;
  background: linear-gradient(135deg, #1e40af 0%, #172554 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.18);
}

.card-action-btn.track-btn:hover {
  background: linear-gradient(135deg, #1e3a8a 0%, #0369a1 100%) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.24);
}

.card-action-btn.cancel-btn {
  flex: 0 0 auto;
  background: #ffffff !important;
  color: #dc2626 !important;
  border: 1px solid #cbd5e1 !important;
}

.card-action-btn.cancel-btn:hover {
  background: #fef2f2 !important;
  border-color: #fca5a5 !important;
  color: #b91c1c !important;
}

.card-action-btn.forward-btn {
  flex: 0 0 auto;
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid rgba(37, 99, 235, 0.35) !important;
}

.card-action-btn.forward-btn:hover {
  background: #dbeafe !important;
  border-color: #1e40af !important;
}

/* Mobile Table Toolbar (< 992px) */
.mobile-table-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid rgba(37, 99, 235, 0.09);
  border-radius: 12px;
}

.mobile-filter-group,
.mobile-per-page-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-filter-group {
  flex: 1 1 240px;
}

.mobile-per-page-group {
  flex: 0 0 auto;
}

.mobile-filter-label {
  margin-bottom: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #475569;
  white-space: nowrap;
}

.mobile-filter-input-wrap {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.mobile-search-input {
  width: 100%;
  height: 38px;
  padding: 6px 32px 6px 12px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.88rem;
  color: #1e293b;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mobile-search-input:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.mobile-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.mobile-search-clear:hover {
  color: #dc2626;
}

.mobile-per-page-select {
  height: 38px;
  padding: 6px 12px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 8px;
  background-color: #ffffff;
  font-size: 0.88rem;
  font-weight: 600;
  color: #1e293b;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mobile-per-page-select:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.08);
}

.mobile-list-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin: 24px auto 12px;
}

.mobile-list-pagination button {
  min-width: 38px;
  height: 38px;
  color: #1e40af;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 10px;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-list-pagination button:hover:not(:disabled) {
  background: #f0f9ff;
  border-color: #1e40af;
}

.mobile-list-pagination button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mobile-list-pagination span {
  font-size: 0.88rem;
  font-weight: 700;
  color: #475569;
}

.status-circle {
  display: inline-block;
  width: 30px;
  height: 30px;
  border-radius: 50%;
}

.status-legend {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.status-legend .status-circle {
  width: 14px;
  height: 14px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #414141;
}

.latest-ticket-card {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.10);
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
}

.report-card-content,
.latest-card-header > div,
.ticket-meta {
  min-width: 0;
  max-width: 100%;
}

.latest-ticket-featured {
  margin-bottom: 0;
}

.latest-ticket-empty {
  background: #f8fafc;
  border: 1px dashed rgba(37, 99, 235, 0.18);
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  color: #6c757d;
}

.latest-ticket-empty .empty-icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.1);
  color: #1e40af;
  font-weight: 700;
  margin-bottom: 12px;
}

.latest-ticket-empty .empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #343a40;
  margin-bottom: 6px;
}

.latest-ticket-empty .empty-text {
  color: #6c757d;
}

.latest-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.ticket-label {
  color: #6c757d;
  text-transform: uppercase;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
}

.ticket-number {
  margin-top: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e40af;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  color: #ffffff;
  font-size: 0.84rem;
  min-width: 110px;
  max-width: 100%;
  box-sizing: border-box;
  text-align: center;
}

.ticket-meta {
  margin-top: 16px;
  color: #495057;
  font-size: 0.95rem;
  line-height: 1.7;
}

.ticket-meta .meta-row {
  margin-bottom: 8px;
}

.ticket-location {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.ticket-location-separator {
  margin: 0 7px;
  color: rgba(37, 99, 235, 0.38);
}

.ticket-department {
  color: #424955;
  font-weight: 800;
}

.ticket-job-type {
  color: #1e40af;
  font-weight: 800;
}

.ticket-actions {
  margin-top: 20px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.card-btn {
  flex: 0 0 auto;
  min-width: 140px;
  max-width: 200px;
  width: auto;
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  cursor: pointer;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.14);
}

.card-btn.track {
  background: #1e40af;
  color: #ffffff;
}

.card-btn.cancel {
  background: #ffffff;
  color: #1e40af;
  border: 1px solid rgba(37, 99, 235, 0.18);
}

.card-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 34px rgba(37, 99, 235, 0.18);
}

.card-btn.cancel:hover {
  background: #f8f1f1;
}

@media (max-width: 768px) {
  .latest-ticket-card {
    width: 100%;
    padding: 16px;
    overflow: hidden;
  }

  .latest-card-header {
    flex-direction: column;
    gap: 14px;
    align-items: stretch;
  }

  .status-chip {
    width: 100%;
    min-width: 0;
  }

  .ticket-actions {
    justify-content: center;
    align-items: stretch;
  }

  .card-btn {
    flex: 1 1 140px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    white-space: normal;
  }
}
</style>

<script>
import { assertListResponse, queueLatestListLoad, disposeListLoad } from "@/utils/asyncFeedback";
import { mapGetters } from "vuex";
import Service from '@/service/api'
import Swal from 'sweetalert2';
import moment from 'moment';
import { formatAdminSwalButton } from '@/utils/sweetalertButtonHelper';
import { formatAvailableTimeDisplay } from "@/i18n/formatAvailableTime";
import { translateSection, translateJobType } from "@/utils/categoryi18n";
import { getTechnicianEffectiveStatus } from "@/utils/ticketViewHelper";


export default {
  name: "Ureportable",
  props: {
    mode: {
      type: String,
      default: 'latest',
      validator: value => ['latest', 'unfinished'].includes(value),
    },
    ticketSource: {
      type: String,
      default: 'user',
      validator: value => ['user', 'technician', 'supervisor', 'admin', 'student_affairs'].includes(value),
    },
    statusFilter: {
      type: String,
      default: '',
    },
    dateRange: {
      type: String,
      default: 'all',
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      ticketsData: [],
      isLoading: false,
      loadError: false,
      hasLoaded: false,
      windowWidth: window.innerWidth,
      tablePage: 1,
      tableItemsPerPage: 10,
      mobilePage: 1,
      mobileSearchQuery: '',
      mobileItemsPerPage: 10,
    };
  },
  computed: {
    itemsPerPageSelectConfig() {
      return {
        label: this.$t('table.itemsPerPageLabel'),
        values: [10, 20, 50]
      };
    },
    ...mapGetters("auth", ["profile"]),
    currentUserId() {
      const userStr = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
      const userObj = userStr ? JSON.parse(userStr) : {};
      return this.profile?._id || this.profile?.id || this.$store?.state?.auth?.user?._id || this.$store?.state?.auth?.user?.id || userObj._id || userObj.id || '';
    },
    isAdminStandard() {
      if (this.ticketSource === 'admin') return true;
      if (this.$route?.meta?.roles && Array.isArray(this.$route.meta.roles)) {
        if (this.$route.meta.roles.includes('admin')) return true;
      }
      const authUser = this.$store?.state?.auth?.user || this.profile;
      if (authUser?.role === 'admin') return true;
      return Boolean(this.$route?.path && this.$route.path.startsWith('/admin/'));
    },
    latestTicket() {
      return this.ticketsData.length ? this.ticketsData[0] : null;
    },
    dateFilteredTickets() {
      return this.ticketsData.filter(ticket => this.isWithinDateRange(ticket));
    },
    unfinishedTickets() {
      const tickets = this.dateFilteredTickets.filter(ticket => !['cancelled', 'cancel'].includes(ticket.status));
      let filtered = tickets;
      if (this.statusFilter === 'total' || this.statusFilter === 'all') {
        filtered = tickets;
      } else if (this.statusFilter === 'unassigned' || this.statusFilter === 'pending') {
        filtered = tickets.filter(ticket => ticket.status === 'pending' || (this.ticketSource === 'admin' && ticket.status === 'assigned' && !ticket.userPart?.category));
      } else if (this.statusFilter === 'new') {
        filtered = tickets.filter(ticket => ['pending', 'assigned'].includes(this.resolveItemStatus(ticket)));
      } else if (this.statusFilter === 'active' || this.statusFilter === 'in_progress') {
        if (this.ticketSource === 'admin') {
          filtered = tickets.filter(ticket => (ticket.status === 'assigned' && Boolean(ticket.userPart?.category)) || ticket.status === 'in_progress');
        } else if (this.ticketSource === 'technician') {
          filtered = tickets.filter(ticket => this.resolveItemStatus(ticket) === 'in_progress');
        } else {
          filtered = tickets.filter(ticket => ticket.status === 'in_progress');
        }
      } else if (this.statusFilter === 'overdue' || this.statusFilter === 'over_sla') {
        filtered = tickets.filter(ticket => this.isOverdue(ticket));
      } else if (this.statusFilter === 'completed' || this.statusFilter === 'done' || this.statusFilter === 'closed') {
        filtered = tickets.filter(ticket => ['done', 'welldone'].includes(ticket.status));
      } else if (this.statusFilter === 'awaitingEval') {
        filtered = tickets.filter(ticket => ticket.status === 'done');
      } else if (this.mode === 'unfinished') {
        filtered = tickets.filter(ticket => !['done', 'welldone'].includes(ticket.status));
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        filtered = filtered.filter(ticket => this.matchesSearch(ticket));
      }
      return filtered;
    },
    mobileFilteredTickets() {
      const q = (this.mobileSearchQuery || '').trim().toLowerCase();
      if (!q) return this.unfinishedTickets;
      return this.unfinishedTickets.filter(ticket => {
        const ticketNum = String(ticket.ticketNumber || '').toLowerCase();
        const subcat = String(this.getSubCategoryName(ticket) || '').toLowerCase();
        const cat = String(this.getCategoryName(ticket) || '').toLowerCase();
        const statusText = String(this.getStatusText(this.resolveItemStatus(ticket)) || '').toLowerCase();
        const assetCode = String(ticket.userPart?.assetCode || '').toLowerCase();
        const onBehalf = String(ticket.userPart?.onBehalfOf || '').toLowerCase();
        return ticketNum.includes(q) ||
          subcat.includes(q) ||
          cat.includes(q) ||
          statusText.includes(q) ||
          assetCode.includes(q) ||
          onBehalf.includes(q);
      });
    },
    mobilePageTickets() {
      const perPage = this.mobileItemsPerPage || 10;
      const start = (this.mobilePage - 1) * perPage;
      return this.mobileFilteredTickets.slice(start, start + perPage);
    },
    mobileTotalPages() {
      const perPage = this.mobileItemsPerPage || 10;
      return Math.max(1, Math.ceil(this.mobileFilteredTickets.length / perPage));
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    allowCancellation() {
      return this.ticketSource === 'user';
    },
    tableFields() {
      void this.$store.state.locale.lang
      return [
        { key: "ticketNumber", label: this.$t('table.user.col.ticketNo'), _style: "width:12%; min-width:110px;" },
        { key: "reported_datetime", label: this.$t('table.user.col.reportedAt'), _style: "width:14%; min-width:120px;" },
        {
          key: "department_category",
          label: (this.$store?.state?.locale?.lang === 'en' ? 'Department' : 'ฝ่าย/แผนก'),
          _style: "width:22%; min-width:160px;"
        },
        { key: "issue_user", label: this.$t('table.user.col.detail'), _style: "width:28%; min-width:190px;" },
        {
          key: "status",
          label: this.$t('table.user.col.status'),
          _style: "width:12%; text-align:center; min-width:105px;",
          _classes: "text-center",
        },
        { key: "actions", label: "", _style: "width:18%; text-align:center; min-width:150px;", _classes: "text-center" },
      ]
    },
  },
  mounted() {
    this.loadTickets();
    window.addEventListener('resize', this.updateWidth);
  },
  beforeDestroy() {
    disposeListLoad(this);
    window.removeEventListener('resize', this.updateWidth);
  },
  watch: {
    profile(val) {
      if (val) this.loadTickets();
    },
    statusFilter() {
      this.mobilePage = 1;
    },
    dateFilteredTickets(val) {
      this.$emit('tickets-loaded', val || []);
    },
    mobileSearchQuery() {
      this.mobilePage = 1;
    },
    mobileItemsPerPage() {
      this.mobilePage = 1;
    },
    tablePage(val) {
      if (this.$refs.cDataTable && this.$refs.cDataTable.page !== val) {
        this.$refs.cDataTable.page = val;
      }
    },
    mobileTotalPages(value) {
      if (this.mobilePage > value) this.mobilePage = value;
    },
  },
  methods: {
    updateWidth() {
      this.windowWidth = window.innerWidth;
    },
    getTicketDate(ticket) {
      if (!ticket) return moment(null);
      const raw = (ticket.userPart && ticket.userPart.dateTimeInformed) || ticket.createdAt || ticket.updatedAt;
      return moment(raw);
    },
    isWithinDateRange(ticket) {
      if (!ticket) return false;
      if (['cancelled', 'cancel'].includes(ticket.status)) return false;
      if (!this.dateRange || this.dateRange === 'all') return true;

      const date = this.getTicketDate(ticket);
      if (!date.isValid()) return false;
      const now = moment();

      switch (this.dateRange) {
        case 'today':
          return date.isSame(now, 'day');
        case 'week':
          return date.isSame(now, 'isoWeek');
        case 'month':
          return date.month() === now.month() && date.year() === now.year();
        case 'year':
          return date.year() === now.year();
        case 'custom': {
          if (!this.startDate || !this.endDate) return true;
          const start = moment(this.startDate).startOf('day');
          const end = moment(this.endDate).endOf('day');
          return date.isBetween(start, end, undefined, '[]');
        }
        default:
          return true;
      }
    },
    matchesSearch(ticket) {
      if (!this.searchQuery || !this.searchQuery.trim()) return true;
      const q = this.searchQuery.trim().toLowerCase();
      const ticketNo = String(ticket.ticketNumber || ticket._id || '').toLowerCase();
      const reporter = String(
        `${ticket.userPart?.name || ''} ${ticket.userPart?.surname || ''} ${ticket.userPart?.username || ''}`
      ).toLowerCase();
      const problem = String(ticket.userPart?.problemDetails || '').toLowerCase();
      const dept = String(ticket.userPart?.category?.departmentName || ticket.userPart?.category?.name || ticket.userPart?.category || '').toLowerCase();
      const jobType = String(ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory || '').toLowerCase();
      const status = String(this.getStatusText(this.resolveItemStatus(ticket)) || '').toLowerCase();

      return (
        ticketNo.includes(q) ||
        reporter.includes(q) ||
        problem.includes(q) ||
        dept.includes(q) ||
        jobType.includes(q) ||
        status.includes(q)
      );
    },
    isOverdue(ticket) {
      if (!ticket?.slaDeadline) return false;
      const end = ['done', 'welldone'].includes(ticket.status)
        ? new Date(ticket.technicianPart?.dateTimeFinished || ticket.updatedAt)
        : new Date();
      return end.getTime() > new Date(ticket.slaDeadline).getTime();
    },
    getOverdueHours(ticket) {
      if (!ticket?.slaDeadline) return 0;
      const end = ['done', 'welldone', 'closed'].includes(ticket.status)
        ? new Date(ticket.technicianPart?.dateTimeFinished || ticket.updatedAt)
        : new Date();
      const deadline = new Date(ticket.slaDeadline);
      const diffMs = end.getTime() - deadline.getTime();
      if (diffMs <= 0) return 0;
      const hours = diffMs / (1000 * 60 * 60);
      return hours >= 1 ? Math.round(hours) : Math.round(hours * 10) / 10;
    },
    getOverdueText(ticket) {
      const hours = this.getOverdueHours(ticket);
      if (hours <= 0) return '';
      const isEn = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase().startsWith('en');
      return isEn ? `Over SLA ${hours}h` : `เกิน SLA ${hours} ชม.`;
    },
    loadTickets() {
      return queueLatestListLoad(this, async isCurrent => {
        this.loadError = false;
        try {
          const userStr = localStorage.getItem('user');
          const userObj = userStr ? JSON.parse(userStr) : {};
          const userId = this.profile?._id || this.profile?.id || userObj._id || userObj.id;
          if (!userId) {
            throw new Error("User ID not found");
          }

          let result;
          if (this.ticketSource === 'admin') {
            const response = await Service.ticket('get', null, { urlAppend: '/admin/all' }).then(assertListResponse);
            const tickets = response.data.success && Array.isArray(response.data.data)
              ? response.data.data
                .filter(ticket => !['cancelled', 'cancel'].includes(ticket.status))
                .map(ticket => ({ ...ticket, _role: 'admin' }))
              : [];
            result = { success: true, data: tickets };
          } else if (this.ticketSource === 'supervisor') {
            const sectionValue = this.profile.section || this.profile.sectionId || this.profile.category;
            const sectionId = sectionValue && (sectionValue._id || sectionValue);
            if (!sectionId) throw new Error('Supervisor section not found');
            const jobTypeParam = this.profile.jobType ? `?jobType=${encodeURIComponent(this.profile.jobType)}` : '';
            const [departmentResponse, reportedResponse, reportedHistoryResponse] = await Promise.all([
              Service.ticket('get', null, { urlAppend: `/supervisor/${sectionId}/all${jobTypeParam}` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/active` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse),
            ]);
            const departmentTickets = departmentResponse.data.success && Array.isArray(departmentResponse.data.data)
              ? departmentResponse.data.data.filter(ticket => !['cancelled', 'cancel'].includes(ticket.status)).map(ticket => ({ ...ticket, _role: 'dept' }))
              : [];
            const existingIds = new Set(departmentTickets.map(ticket => String(ticket._id)));
            const reportedTickets = [
              ...(reportedResponse.data.success && Array.isArray(reportedResponse.data.data) ? reportedResponse.data.data : []),
              ...(reportedHistoryResponse.data.success && Array.isArray(reportedHistoryResponse.data.data) ? reportedHistoryResponse.data.data : []),
            ].filter(ticket => !existingIds.has(String(ticket._id)) && !['cancelled', 'cancel'].includes(ticket.status))
              .filter((ticket, index, list) => list.findIndex(item => String(item._id) === String(ticket._id)) === index)
              .map(ticket => ({ ...ticket, _role: 'reporter' }));
            result = { success: true, data: [...departmentTickets, ...reportedTickets] };
          } else if (this.ticketSource === 'user' || this.ticketSource === 'student_affairs') {
            const [activeRes, historyRes] = await Promise.all([
              Service.ticket('get', null, { urlAppend: `/user/${userId}/active` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse),
            ]);
            const activeTickets = activeRes.data && Array.isArray(activeRes.data.data) ? activeRes.data.data : [];
            const historyTickets = historyRes.data && Array.isArray(historyRes.data.data) ? historyRes.data.data : [];
            const seenIds = new Set(activeTickets.map(t => String(t._id)));
            const userTickets = [
              ...activeTickets,
              ...historyTickets.filter(t => !seenIds.has(String(t._id)))
            ].filter(ticket => !['cancelled', 'cancel'].includes(ticket.status));
            result = { success: true, data: userTickets };
          } else {
            // technician
            const [assignedRes, reportedActiveRes, reportedHistoryRes] = await Promise.all([
              Service.ticket('get', null, { urlAppend: `/technician/${userId}/assigned` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/active` }).then(assertListResponse),
              Service.ticket('get', null, { urlAppend: `/user/${userId}/history` }).then(assertListResponse),
            ]);

            const assignedTickets = assignedRes.data && assignedRes.data.success && Array.isArray(assignedRes.data.data)
              ? assignedRes.data.data
                  .filter(t => {
                    if (['cancelled', 'cancel'].includes(t.status)) return false;
                    const assignments = t.supervisorPart?.assignments || [];
                    const myAssign = assignments.find(a => {
                      const id = a.assignee?._id ? String(a.assignee._id) : String(a.assignee);
                      return id === String(userId);
                    });
                    if (myAssign && myAssign.status === 'rejected') return false;
                    return true;
                  })
                  .map(t => ({ ...t, _role: 'assignee' }))
              : [];

            const existingIds = new Set(assignedTickets.map(t => String(t._id)));
            const reportedTickets = [
              ...(reportedActiveRes.data && reportedActiveRes.data.success && Array.isArray(reportedActiveRes.data.data) ? reportedActiveRes.data.data : []),
              ...(reportedHistoryRes.data && reportedHistoryRes.data.success && Array.isArray(reportedHistoryRes.data.data) ? reportedHistoryRes.data.data : []),
            ].filter(t => !existingIds.has(String(t._id)) && !['cancelled', 'cancel'].includes(t.status))
              .filter((t, index, list) => list.findIndex(item => String(item._id) === String(t._id)) === index)
              .map(t => ({ ...t, _role: 'reporter' }));

            result = { success: true, data: [...assignedTickets, ...reportedTickets] };
          }

          if (!isCurrent()) return;
          if (result.success) {
            const tickets = Array.isArray(result.data) ? result.data : [];
            tickets.sort((a, b) => {
              const aDate = a.userPart && a.userPart.dateTimeInformed ? new Date(a.userPart.dateTimeInformed).getTime() : 0;
              const bDate = b.userPart && b.userPart.dateTimeInformed ? new Date(b.userPart.dateTimeInformed).getTime() : 0;
              return bDate - aDate;
            });
            this.ticketsData = tickets;
            this.$emit('tickets-loaded', this.ticketsData);
            if (this.ticketsData.length > 0) {
              this.$emit('latest-ticket-loaded', this.ticketsData[0]);
            } else {
              this.$emit('latest-ticket-loaded', null);
            }
          }
          this.hasLoaded = true;
        } catch (error) {
          if (!isCurrent()) return;
          console.error("Failed to load tickets:", error);
          this.loadError = true;
        }
      });
    },
    resolveItemStatus(ticket) {
      if (!ticket) return 'pending';
      if (this.ticketSource === 'technician' && ticket._role !== 'reporter') {
        return getTechnicianEffectiveStatus(ticket, this.currentUserId);
      }
      return ticket.status || 'pending';
    },
    showDetail(item, isEvaluate) {
      this.$emit("show-detail", {
        ...item,
        isEvaluateMode: isEvaluate,
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const suf = this.$store.state.locale.lang === 'th' ? ' น.' : '';
      return `${day}/${month}/${year} ${hours}:${minutes}${suf}`;
    },
    getCategoryName(item) {
      const lang = this.$store?.state?.locale?.lang || "th";
      const cat = item.userPart && item.userPart.category;
      if (!cat) return this.$t('table.user.unknownDept');
      return translateSection(cat, lang);
    },
    getAvailableTime(item) {
      const t = item.userPart && item.userPart.availableTime;
      if (!t) return "-";
      const lang = this.$store.state.locale.lang === "en" ? "en" : "th";
      return formatAvailableTimeDisplay(t, lang) + this.$t("ticketDetail.timeSuffix");
    },
    getSubCategoryName(item) {
      const lang = this.$store?.state?.locale?.lang || "th";
      const sub = item.userPart && item.userPart.subCategory;
      if (!sub) return "-";

      const cat = item.userPart && item.userPart.category;
      const job = typeof sub === 'object'
        ? sub
        : (cat?.subCategories || []).find(
            (j) =>
              (item.userPart?.subCategoryId && String(j._id || j.id) === String(item.userPart.subCategoryId)) ||
              j.name === sub ||
              (j.previousNames || []).includes(sub)
          );

      return translateJobType(job || sub, lang);
    },
    getProblemDetails(item) {
      if (!item) return "-";
      const desc = (item.userPart && item.userPart.problemDetails) || item.problemDetails || item.details || item.description || "";
      return desc && desc.trim() ? desc.trim() : "-";
    },
    getDepartmentName(item) {
      return this.getCategoryName(item);
    },
    getJobType(item) {
      return this.getSubCategoryName(item);
    },
    getStatusText(status) {
      const table = this.ticketSource === 'technician'
        ? 'tech'
        : this.ticketSource === 'supervisor' ? 'supervisor'
          : this.ticketSource === 'admin' ? 'admin' : 'user'
      const key = `table.${table}.status.${status}`
      const tr = this.$t(key)
      return tr !== key ? tr : status
    },
    legendKey(number) {
      const table = this.ticketSource === 'technician'
        ? 'tech'
        : this.ticketSource === 'supervisor' ? 'supervisor'
          : this.ticketSource === 'admin' ? 'admin' : 'user'
      return `table.${table}.legend${number}`
    },
    getBadgeStyle(status) {
      switch (status) {
        case 'pending':
          return {
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderColor: '#fca5a5',
            dotColor: '#ef4444',
          };
        case 'assigned':
          return this.ticketSource === 'technician'
            ? {
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderColor: '#fca5a5',
                dotColor: '#ef4444',
              }
            : {
                backgroundColor: '#ffedd5',
                color: '#9a3412',
                borderColor: '#fdba74',
                dotColor: '#f97316',
              };
        case 'in_progress':
          return {
            backgroundColor: '#fef3c7',
            color: '#92400e',
            borderColor: '#fde68a',
            dotColor: '#f59e0b',
          };
        case 'done':
        case 'welldone':
          return {
            backgroundColor: '#dcfce7',
            color: '#166534',
            borderColor: '#86efac',
            dotColor: '#22c55e',
          };
        case 'cancelled':
        case 'cancel':
          return {
            backgroundColor: '#f1f5f9',
            color: '#64748b',
            borderColor: '#cbd5e1',
            dotColor: '#94a3b8',
          };
        default:
          return {
            backgroundColor: '#f8fafc',
            color: '#475569',
            borderColor: '#e2e8f0',
            dotColor: '#94a3b8',
          };
      }
    },
    getBadge(status) {
      const badgeMap = {
        pending: "#e55353",
        assigned: this.ticketSource === 'technician' ? "#e55353" : "#fd7e14",
        in_progress: "#ffeb3b",
        done: "#2eb85c",
        welldone: "#2eb85c",
        cancelled: "#cdcdcd",
      };
      return badgeMap[status] || "#f8f9fa";
    },
    isTicketReporter(ticket) {
      if (!ticket) return false;
      if (this.ticketSource === 'user' || this.ticketSource === 'student_affairs') return true;
      if (ticket._role === 'reporter') return true;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const userId = String(this.profile?._id || this.profile?.id || userObj._id || userObj.id || '');
      if (!userId) return false;
      const userPart = ticket.userPart || {};
      const rawUser = userPart.user;
      const reporterId = String(
        userPart.userId ||
        userPart.id ||
        userPart._id ||
        (typeof rawUser === 'object' && rawUser !== null ? (rawUser._id || rawUser.id) : rawUser) ||
        ''
      );
      return Boolean(reporterId && userId === reporterId);
    },
    canCancelTicket(ticket) {
      if (!ticket) return false;
      if (!this.isTicketReporter(ticket)) return false;
      if (['done', 'welldone', 'cancelled', 'cancel', 'in_progress'].includes(ticket.status)) {
        return false;
      }
      const assignments = ticket.supervisorPart?.assignments || [];
      const hasAcceptedAssignment = assignments.some(a => a.status === 'accepted');
      const hasAcceptedDate = Boolean(ticket.technicianPart?.dateTimeAccepted);
      if (hasAcceptedAssignment || hasAcceptedDate) {
        return false;
      }
      return ['pending', 'assigned'].includes(ticket.status);
    },
    async cancelTicket(item) {
      const result = await Swal.fire({
        title: this.$t('table.user.swalCancelTitle'),
        html: this.$t('table.user.swalCancelHtml'),
        input: 'textarea',
        inputPlaceholder: this.$t('table.user.swalPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('table.user.swalPlaceholder')
        },
        showCancelButton: true,
        confirmButtonText: this.$t('table.user.btnConfirmCancel'),
        cancelButtonText: this.$t('table.user.btnBack'),
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return this.$t('table.user.swalReasonRequired');
          }
        }
      });

      if (result.isConfirmed) {
        try {
          const response = await Service.ticket('put', {
            userId: this.profile._id,
            cancelReason: result.value,
          }, { urlAppend: `/${item._id}/cancel` });
          const data = response.data;

          if (data.success) {
            Swal.fire({
              icon: 'success',
              title: this.$t('table.user.swalSuccess'),
              text: this.$t('table.user.swalSuccessText'),
              showConfirmButton: false,
              timer: 1600,
              timerProgressBar: true,
              allowOutsideClick: false
            });
            this.loadTickets();
          } else {
            Swal.fire('Error', data.message || this.$t('table.user.swalErrCancel'), 'error');
          }
        } catch (error) {
          console.error("Cancel error:", error);
          Swal.fire('Error', this.$t('table.user.swalErrNetwork'), 'error');
        }
      }
    },
    jumpToTicket(ticketId, ticketNo) {
      if (!ticketId && !ticketNo) return;
      const targetId = String(ticketId || '').toLowerCase();
      const targetNo = String(ticketNo || '').toLowerCase();

      if (this.$refs.cDataTable) {
        this.$refs.cDataTable.tableFilterState = '';
      }
      this.mobileSearchQuery = '';

      const items = (this.$refs.cDataTable && this.$refs.cDataTable.sortedItems) || this.unfinishedTickets;
      const idx = items.findIndex(t => {
        const id = String(t._id || t.id || '').toLowerCase();
        const no = String(t.ticketNumber || (id ? id.substring(0, 8).toUpperCase() : '')).toLowerCase();
        return (targetId && id === targetId) || (targetNo && (no === targetNo || id.substring(0, 8).toUpperCase() === targetNo.toUpperCase()));
      });

      if (idx !== -1) {
        const perPage = (this.$refs.cDataTable && this.$refs.cDataTable.perPageItems) || this.tableItemsPerPage || 10;
        const targetPage = Math.floor(idx / perPage) + 1;
        this.tablePage = targetPage;
        if (this.$refs.cDataTable) {
          this.$refs.cDataTable.page = targetPage;
        }
        this.mobilePage = Math.floor(idx / this.mobileItemsPerPage) + 1;

        this.$nextTick(() => {
          if (this.$refs.cDataTable && this.$refs.cDataTable.page !== targetPage) {
            this.$refs.cDataTable.page = targetPage;
          }
          const links = this.$el ? this.$el.querySelectorAll('.pagination .page-item .page-link') : [];
          links.forEach(link => {
            if (link.textContent && link.textContent.trim() === String(targetPage)) {
              link.click();
            }
          });
        });

        this.retryScrollToAndHighlight([
          'm-ticket-row-' + ticketNo,
          'm-ticket-row-' + ticketId,
          'ticket-row-' + ticketNo,
          'ticket-row-' + ticketId,
          ticketNo,
          ticketId
        ]);
      }
    },
    scrollElementToCenter(target) {
      if (!target) return;
      const scrollContainers = [
        document.querySelector('.c-body'),
        document.querySelector('.c-main'),
        document.querySelector('.role-theme-app'),
        document.scrollingElement,
        document.documentElement,
        document.body
      ];
      const container = scrollContainers.find(
        c => c && c.scrollHeight > c.clientHeight && (typeof window === 'undefined' || window.getComputedStyle(c).overflowY !== 'hidden')
      );
      const bottomNavHeight = 90;
      const headerHeight = 70;

      if (container && container !== document.documentElement && container !== document.body) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const currentScroll = container.scrollTop;
        const relativeTargetTop = targetRect.top - containerRect.top + currentScroll;
        const effectiveHeight = Math.max(100, containerRect.height - bottomNavHeight);
        const idealTop = relativeTargetTop - (effectiveHeight / 2) + (targetRect.height / 2);

        container.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      } else {
        const targetRect = target.getBoundingClientRect();
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const effectiveHeight = Math.max(100, windowHeight - headerHeight - bottomNavHeight);
        const idealTop = (currentScrollY + targetRect.top) - headerHeight - (effectiveHeight / 2) + (targetRect.height / 2);

        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: 'smooth'
        });
      }
    },
    scrollToAndHighlight(ids) {
      const isVisible = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        if (el.offsetParent === null && (typeof window === 'undefined' || window.getComputedStyle(el).position !== 'fixed')) {
          return false;
        }
        return true;
      };

      let foundTarget = null;
      for (const id of ids) {
        if (!id) continue;
        const rawId = String(id).replace(/^ticket-row-|^m-ticket-row-/, '');
        const candidates = [
          document.getElementById('m-ticket-row-' + rawId),
          document.getElementById('ticket-row-' + rawId),
          document.getElementById(id),
          ...(this.$el ? Array.from(this.$el.querySelectorAll(`[data-ticket-id="${rawId}"], [data-ticket-no="${rawId}"]`)) : [])
        ].filter(Boolean);

        for (const el of candidates) {
          const target = el.closest('.active-mobile-card') ||
                         el.closest('.history-mobile-card') ||
                         el.closest('.mobile-ticket-card') ||
                         el.closest('tr') ||
                         el;
          if (isVisible(target)) {
            target.classList.remove('match-highlight-active');
            void target.offsetWidth;
            target.classList.add('match-highlight-active');
            setTimeout(() => {
              target.classList.remove('match-highlight-active');
            }, 3600);
            if (!foundTarget) {
              foundTarget = target;
            }
          }
        }
      }

      if (foundTarget) {
        this.scrollElementToCenter(foundTarget);
        return true;
      }
      return false;
    },
    retryScrollToAndHighlight(ids, attempts = 10) {
      this.$nextTick(() => {
        const found = this.scrollToAndHighlight(ids);
        if (!found && attempts > 0) {
          setTimeout(() => {
            this.retryScrollToAndHighlight(ids, attempts - 1);
          }, 100);
        }
      });
    },
  },
};
</script>
